import { HelpIconProps, Radio } from '@dixa/lib-shared-habla';
import { PropsWithClassName } from 'lib-shared-types-utils';
import omit from 'lodash/omit';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { HookFormBaseProps, useHookForm } from 'utils/hookForm';

type HookFormRadioProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = HookFormBaseProps<
  TFieldValues,
  TName,
  Pick<ComponentPropsWithoutRef<typeof Radio>, 'disabled'>
> &
  PropsWithClassName & {
    /** The label. */
    label?: ReactNode;
    /** The help icon and tooltip. */
    helpIcon?: HelpIconProps;
    /**
     * The value of the radio button when it's clicked this is the value that is
     * set in the form state. The radio is considered checked if the value of
     * the radio button is equal to the value of the form state.
     */
    radioValue: string;
  };

/** Represents the React Hook Form variant of the {@link Radio} component. */
export const HookFormRadio = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormRadioProps<TFieldValues, TName>,
): JSX.Element => {
  const {
    controller: { field },
  } = useHookForm(props);

  const isChecked = field.value === props.radioValue;

  return (
    <Radio
      type="radio"
      {...omit(field, 'value')}
      {...(props.componentProps || {})}
      name={props.name}
      className={props.className}
      checked={isChecked}
      value={props.radioValue}
    />
  );
};
